// src/components/Public.js
import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";

const Public = () => {
  const gettingRef = useRef(null);

  // Auto-scroll to the "Cara Menggunakan Pertama kali" section on first load
  useEffect(() => {
    // small delay to let layout settle
    setTimeout(() => gettingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
  }, []);

  const scrollToGetting = () => gettingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <section className="font-sans bg-white text-gray-900 min-h-screen flex flex-col">
      {/* Top Navigation */}
      <nav className="flex justify-between items-center px-8 py-6 border-b border-gray-200 bg-white shadow-sm">
        <div className="text-2xl font-semibold tracking-tight text-gray-900">
          Put Your Company/Product Logo and Brand Here!
        </div>
        <div className="flex gap-6">
          <button onClick={scrollToGetting} className="text-indigo-600 hover:text-indigo-800 font-medium transition">
            Getting Started
          </button>
          <Link to="/about" className="text-gray-700 hover:text-black font-medium transition">
            About
          </Link>
          <Link to="/login" className="text-gray-700 hover:text-black font-medium transition">
            Login
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-start px-4 py-12">
        {/* Hero Section */}
        <div className="max-w-2xl w-full text-center mb-8">
          <h1 className="text-5xl font-bold mb-4 tracking-tight">
            Welcome to Web Pack Starter Kit by Mudah Tech Team
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Empowering businesses and education with intelligent solutions and seamless technology integration.
          </p>
        </div>

        {/* --- Prominent Getting Started / Cara Menggunakan Pertama kali --- */}
        <div
          ref={gettingRef}
          id="getting-started"
          className="max-w-3xl w-full bg-gradient-to-r from-indigo-700 to-indigo-600 text-white rounded-2xl shadow-lg p-8 mb-10 border-l-4 border-indigo-400"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold mb-3">Cara Menggunakan Pertama kali</h2>
              <p className="mb-4 opacity-90">
                Ikuti langkah singkat berikut agar project bisa dijalankan dan Anda dapat mulai mengembangkan:
              </p>

              <ol className="list-decimal list-inside space-y-3 text-sm">
                <li>
                  Install dependencies:
                  <div className="mt-2">
                    <code className="bg-white/10 rounded px-2 py-1 text-xs">npm install</code>
                  </div>
                </li>

                <li>
                  Jika Anda ingin mengubah port development (default 3000 → 3001), buat file <code className="bg-white/10 rounded px-2 py-1 text-xs">.env</code> di root dan tambahkan:
                  <div className="mt-2">
                    <code className="bg-white/10 rounded px-2 py-1 text-xs block">PORT=3001</code>
                  </div>
                </li>

                <li>
                  Jalankan development server:
                  <div className="mt-2">
                    <code className="bg-white/10 rounded px-2 py-1 text-xs">npm start</code>
                  </div>
                </li>

                <li>
                  Buka aplikasi di browser. Jika Anda mengikuti langkah 2, buka <span className="font-medium">http://localhost:3001</span>.
                </li>

                <li>
                  Login dan kunjungi dashboard untuk memeriksa data contoh dan konfigurasi.
                </li>
              </ol>
            </div>

            <div className="w-44 flex-shrink-0 text-right">
              <div className="bg-white/10 rounded p-3">
                <div className="text-xs opacity-80 mb-2">Quick links</div>
                <div className="flex flex-col gap-2">
                  <a href="#getting-started" onClick={scrollToGetting} className="text-white/95 bg-white/6 px-3 py-2 rounded text-sm">Show Instructions</a>
                  <Link to="/login" className="text-indigo-700 bg-white px-3 py-2 rounded text-sm text-center font-medium">Login</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-sm opacity-90">
            Notes:
            <ul className="list-disc list-inside mt-2">
              <li>File utama untuk landing ini berada di <code className="bg-white/10 rounded px-1">src/components/Public.js</code>.</li>
              <li>Dokumentasi dan repo ada pada README atau halaman docs pada repository.</li>
            </ul>
          </div>
        </div>

        {/* Remaining sections (Mission / Products) */}
        <div className="max-w-3xl w-full bg-white rounded-2xl shadow p-8 mb-10">
          <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
          <p className="text-gray-700 mb-6">
            To simplify technology adoption and deliver innovative, reliable, and user-friendly solutions that drive business growth.
          </p>
          <h2 className="text-2xl font-semibold mb-4">Our Vision</h2>
          <p className="text-gray-700">
            To be the leading partner for digital transformation, making advanced technology accessible and beneficial for every organization.
          </p>
        </div>

        <div className="max-w-3xl w-full bg-white rounded-2xl shadow p-8">
          <h2 className="text-2xl font-semibold mb-6">Products & Services</h2>
          <ul className="space-y-6">
            <li>
              <h3 className="text-lg font-medium mb-1">Business Intelligence App</h3>
              <p className="text-gray-700">
                Powerful analytics platform for actionable insights, reporting, and data-driven decision making.
              </p>
            </li>
            <li>
              <h3 className="text-lg font-medium mb-1">Open AI Integration</h3>
              <p className="text-gray-700">
                Seamless integration of AI capabilities to automate workflows, enhance productivity, and unlock new possibilities.
              </p>
            </li>
            <li>
              <h3 className="text-lg font-medium mb-1">Custom App Development</h3>
              <p className="text-gray-700">
                Tailored software solutions designed to meet your unique business needs, from concept to deployment.
              </p>
            </li>
          </ul>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto py-8 border-t border-gray-200 text-center bg-white">
        <p className="text-gray-500 text-sm">
          &copy; {new Date().getFullYear()} PT Teknologi Mudah Selaras. All rights reserved.
        </p>
      </footer>
    </section>
  );
};

export default Public;
